export type SlaState = "met" | "ok" | "warning" | "breached" | "paused";

export interface SlaInfo {
  response_due_at: string | null;
  resolution_due_at: string | null;
  response_state: SlaState;
  resolution_state: SlaState;
  response_breached: boolean;
  resolution_breached: boolean;
}

/**
 * SLA badge presentation.
 *
 * Always a tone plus a text label — an SLA pill drives escalation decisions,
 * and colour alone is unreadable to the ~8% of men with colour-vision
 * deficiency and invisible on a printed job sheet.
 */
export function slaBadge(
  state: SlaState,
  dueAt: string | null,
  now: Date = new Date(),
): { tone: "muted" | "success" | "warning" | "danger"; label: string } {
  switch (state) {
    case "breached":
      return { tone: "danger", label: `Breached ${remaining(dueAt, now)}` };
    case "warning":
      return { tone: "warning", label: `Due in ${remaining(dueAt, now)}` };
    case "met":
      return { tone: "success", label: "Met" };
    case "paused":
      // Not "on track": the clock is stopped because the customer owes us
      // something, and that distinction decides who is chased.
      return { tone: "muted", label: "Paused" };
    default:
      return { tone: "success", label: dueAt ? `Due in ${remaining(dueAt, now)}` : "—" };
  }
}

/** Compact duration to or since a deadline: "2h 15m", "3d 4h". */
function remaining(dueAt: string | null, now: Date): string {
  if (!dueAt) return "—";
  const diffMs = Math.abs(new Date(dueAt).getTime() - now.getTime());
  const minutes = Math.floor(diffMs / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/** True when the ticket needs someone's attention now. */
export function needsAttention(sla: SlaInfo): boolean {
  return (
    sla.response_state === "breached" ||
    sla.resolution_state === "breached" ||
    sla.response_state === "warning" ||
    sla.resolution_state === "warning"
  );
}
